import { createReducer, on } from '@ngrx/store';
import { WorkerCertificate, CertificationAlert, CertificationMonitorSummary } from '../safety-certification.models';
import { CertificationActions } from './certification-monitoring.actions';

/**
 * Certification Monitoring State
 */
export interface CertificationMonitoringState {
  allCertificates: WorkerCertificate[];
  expiringSoon: {
    certificates: WorkerCertificate[];
    daysOut: number;
  };
  expiredCertificates: WorkerCertificate[];
  summary: CertificationMonitorSummary | null;
  alerts: CertificationAlert[];

  // Loading states
  loading: boolean;
  loadingSummary: boolean;
  loadingAlerts: boolean;

  // Error states
  error: string | null;
  errorSummary: string | null;
  errorAlerts: string | null;

  // Metadata
  lastLoadedProjectId: number | null;
  lastUpdated: Date | null;
}

const initialState: CertificationMonitoringState = {
  allCertificates: [],
  expiringSoon: {
    certificates: [],
    daysOut: 30
  },
  expiredCertificates: [],
  summary: null,
  alerts: [],
  loading: false,
  loadingSummary: false,
  loadingAlerts: false,
  error: null,
  errorSummary: null,
  errorAlerts: null,
  lastLoadedProjectId: null,
  lastUpdated: null
};

export const certificationMonitoringReducer = createReducer(
  initialState,
  // Load all certifications
  on(CertificationActions.loadProjectCertifications, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CertificationActions.loadProjectCertificationsSuccess, (state, { certificates }) => ({
    ...state,
    allCertificates: certificates,
    loading: false,
    error: null,
    lastUpdated: new Date()
  })),
  on(CertificationActions.loadProjectCertificationsError, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load expiring soon
  on(CertificationActions.loadExpiringSoon, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CertificationActions.loadExpiringSoonSuccess, (state, { certificates, daysOut }) => ({
    ...state,
    expiringSoon: { certificates, daysOut },
    loading: false,
    error: null,
    lastUpdated: new Date()
  })),
  on(CertificationActions.loadExpiringSoonError, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load expired
  on(CertificationActions.loadExpired, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CertificationActions.loadExpiredSuccess, (state, { certificates }) => ({
    ...state,
    expiredCertificates: certificates,
    loading: false,
    error: null,
    lastUpdated: new Date()
  })),
  on(CertificationActions.loadExpiredError, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load summary
  on(CertificationActions.loadCertificationSummary, state => ({
    ...state,
    loadingSummary: true,
    errorSummary: null
  })),
  on(CertificationActions.loadCertificationSummarySuccess, (state, { summary }) => ({
    ...state,
    summary,
    loadingSummary: false,
    errorSummary: null,
    lastUpdated: new Date()
  })),
  on(CertificationActions.loadCertificationSummaryError, (state, { error }) => ({
    ...state,
    loadingSummary: false,
    errorSummary: error
  })),

  // Load alerts
  on(CertificationActions.loadCertificationAlerts, state => ({
    ...state,
    loadingAlerts: true,
    errorAlerts: null
  })),
  on(CertificationActions.loadCertificationAlertsSuccess, (state, { alerts }) => ({
    ...state,
    alerts,
    loadingAlerts: false,
    errorAlerts: null,
    lastUpdated: new Date()
  })),
  on(CertificationActions.loadCertificationAlertsError, (state, { error }) => ({
    ...state,
    loadingAlerts: false,
    errorAlerts: error
  })),

  // Clear
  on(CertificationActions.clearCertifications, () => initialState)
);
